import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { AuthTokens, User } from '@/entities/user';
import { http } from '@/shared/api/httpClient';
import { unwrapEnvelopeData } from '@/shared/api/apiEnvelope';
import { env } from '@/shared/config/env';
import {
  clearTokens,
  getRefreshToken,
  setPersistedUserSnapshot,
  setTokens,
  useSessionStore,
} from '@/shared/lib/auth';

import type { RemoteAuthUser, RemoteSessionPayload } from './remoteAuthTypes';
import { mapRemoteAuthUserToUser } from './mapRemoteAuthUser';
import type { SuperAdminLoginInput } from '../schemas/superAdminLoginSchema';
import { authKeys } from './keys';

interface LoginResult extends AuthTokens {
  user: User;
}

async function postSuperAdminLogin(input: SuperAdminLoginInput): Promise<LoginResult> {
  const res = await http.post<unknown>(
    env.authSuperAdminLoginPath,
    {
      email: input.username.trim(),
      password: input.password,
    },
    { idempotency: false },
  );
  const payload = unwrapEnvelopeData<RemoteSessionPayload>(res.data, res.status);
  return {
    accessToken: payload.accessToken,
    refreshToken: payload.refreshToken,
    user: mapRemoteAuthUserToUser(payload.user),
  };
}

export function useMeQuery(enabled: boolean) {
  const setUser = useSessionStore((s) => s.setUser);

  return useQuery({
    queryKey: authKeys.me(),
    queryFn: async () => {
      const res = await http.get<unknown>(env.authProfilePath);
      const apiUser = unwrapEnvelopeData<RemoteAuthUser>(res.data, res.status);
      const user = mapRemoteAuthUserToUser(apiUser);
      setUser(user);
      setPersistedUserSnapshot(user);
      return user;
    },
    enabled,
    retry: false,
    staleTime: 5 * 60_000,
  });
}

/** Super-admin username + password — `POST …/auth/super-admin/login` */
export function useSuperAdminLoginMutation() {
  const queryClient = useQueryClient();
  const setUser = useSessionStore((s) => s.setUser);

  return useMutation({
    mutationKey: ['auth', 'login', 'super-admin'],
    mutationFn: postSuperAdminLogin,
    onSuccess: ({ accessToken, refreshToken, user }) => {
      setTokens({ accessToken, refreshToken });
      setUser(user);
      setPersistedUserSnapshot(user);
      queryClient.setQueryData(authKeys.me(), user);
    },
  });
}

export function useLogoutMutation() {
  const queryClient = useQueryClient();
  const clear = useSessionStore((s) => s.clear);

  return useMutation({
    mutationKey: ['auth', 'logout'],
    mutationFn: async () => {
      const refreshToken = getRefreshToken();
      try {
        await http.post(env.authLogoutPath, refreshToken ? { refreshToken } : {}, {
          idempotency: false,
        });
      } catch {
        // Swallow — local cleanup must still run.
      }
    },
    onSettled: () => {
      clearTokens();
      clear();
      queryClient.clear();
    },
  });
}

/** @deprecated Use `useSuperAdminLoginMutation` — kept for barrel compatibility during refactors. */
export const useLoginMutation = useSuperAdminLoginMutation;
